import { Component, Input, ViewEncapsulation } from "@angular/core";
import { MatDialog as MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
// @ts-ignore
import _ from "lodash";
import { AlertService } from "src/app/services/alert.service";
import { AuthService } from "src/app/services/auth.service";
import { LoaderService } from "src/app/services/loader.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialog/confirm-dialog/confirm-dialog.component";
import { LivecoAPIService } from "src/app/services/liveco-api.service";
import { ProofreadingService } from "src/app/services/proofreading.service";
import { ExternStateName, ExternStatePayloadFlag, InternStateName, StateConditionOptions } from "src/app/models/states.model";
import { ApprovalAssessments, FlowType, IFiles, Proofreading } from "src/app/models/proofreading.model";
import { MembersService } from "src/app/services/member.service";
import { MemberPermission, MemberStatus } from "src/app/models/projects/submodels/members.model";
import { ProductionTextsService } from "src/app/services/production-texts.service";
import { UtilsService } from "src/app/services/utils.service";
import { ViewComponent } from "src/app/shared/pages/view/view.component";
import { TranslateService } from '@ngx-translate/core';
import { ProjectService } from "src/app/services/project.service";
import { State } from "src/app/models/projects/project.model";
import { StateMachineService } from "src/app/services/machine-state.service";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "app-state-machine-extern",
    templateUrl: "./state-machine-extern.component.html",
    styleUrls: ["./state-machine-extern.component.scss"],
})
export class StateMachineExternComponent extends ViewComponent {

    @Input() readOnly: boolean = false;
    @Input() files: any;


    get MEMBER_STATUS() {
        return MemberStatus;
    }

    get EXTERN_STATE_NAME() {
        return ExternStateName;
    }

    get proofreading(): Proofreading | undefined {
        return this.s_proofreadings.current;
    }

    get flow() {
        return this.proofreading?.flow || FlowType.EXE;
    }

    get externState() {
        return this.proofreading?.state;
    }

    get internState() {
        return this.proofreading?.intern?.currentState;
    }

    get nextStates(): ExternStateName[] | undefined {
        return this.externState?.next ? Object.keys(this.externState?.next) as ExternStateName[] : undefined;
    }

    get main_approver() {
        return this.s_members.getMembersByPerms([MemberPermission.APPROVAL])[0];
    }

    get some_preempt_rejected() {
        return this.s_members.members.filter(m => m.hasPreemptiveRight).some(m => m.status === MemberStatus.REJECT) || false;
    }

    get members() {
        return this.s_members.members;
    }
    get current_member() {
        return this.s_members.current_member;
    };

    get isApproverDocument(): boolean {
        return this.s_members.isApproverDocument;
    }
    get isApproverInfo(): boolean {
        return this.s_members.isApproverInfo;
    }
    get hasDecision(): boolean {
        return this.s_members.hasDecision;
    }

    // TEXTES RELECTURE
    get has_textes_flow() {
        return this.flow === FlowType.EXE || this.flow === FlowType.EZD_EXE;
    }

    // STATES
    get approved() {
        return this.externState?.slug === ExternStateName.APPROVED;
    }

    get intern_delivered() {
        return this.internState?.slug === InternStateName.DELIVERED;
    }

    get historyItem() {
        return {
            intern: this.s_proofreadings.currentHistory?.intern,
            extern: this.s_proofreadings.currentHistory?.extern,
            approval: this.s_proofreadings.currentHistory?.approval,
        };
    }

    get lastStates() {
        return this.historyItem?.extern?.getLastStates().slice(0, 3);
    }

    get canApprove(): boolean {
        return this.current_member?.status === MemberStatus.PROGRESS
            && (this.isApproverDocument && this.externState?.hasPayloadFlag(ExternStatePayloadFlag.CUSTOMER_CAN_APPROVE)
                || (!this.isApproverDocument && this.isApproverInfo && this.externState?.hasPayloadFlag(ExternStatePayloadFlag.CUSTOMER_INFO_CAN_APPROVE)));
    }

    get computeUserAttributes() {
        return [
            ...(this.s_auth.user?.attributes_name || []),
            this.current_member?.hasApproDocument ? StateConditionOptions.APPROVER_DOCUMENT : "",
        ];
    }

    get project_archived() {
        return this.s_project.current?.state === State.ARCHIVED;
    }

    // ORDER
    get hasOrder() {
        return !this.current_member?.hasApproDocument && !!this.members.filter(m => !_.isNil(m.order)).length;
    }

    get isCurrentOrder() {
        return !this.hasOrder || (this.current_member?.order || 0) === (this.proofreading?.current_order || 0);
    }

    get canCustomerInfoApprove() {
        return this.current_member?.status === MemberStatus.PENDING && this.externState?.hasPayloadFlag(ExternStatePayloadFlag.CUSTOMER_INFO_CAN_APPROVE);
    }

    get locked() {
        return this.isClient() && !this.s_state_machine.hasPayloadFlag(ExternStatePayloadFlag.CUSTOMER_CAN_EDIT, (this.externState?.slug || ExternStateName.INIT) as ExternStateName, this.flow, 'extern');
    }

    get readOnlyText(): boolean {
        return this.locked
            // Only clients are restricted
            || (this.isClient() && (!this.s_members.hasPermission([MemberPermission.TEXT_WRITE], '&') || this.s_members.hasStatus([MemberStatus.READ_ONLY], '&')))
            || this.isOperator(); // deny editing if operator
    };


    constructor(
        protected dialog: MatDialog,
        protected s_alert: AlertService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected s_liveco: LivecoAPIService,
        protected s_auth: AuthService,
        protected s_loader: LoaderService,
        public s_proofreadings: ProofreadingService,
        protected s_productionTexts: ProductionTextsService,
        protected s_members: MembersService,
        
        protected s_project: ProjectService,
        protected s_state_machine: StateMachineService,
    ) {
        super(route, router, s_alert, s_loader, s_auth);
    }

    /**
     * NAV
     */
    async onNextState(nextState: ExternStateName) {
        if (!this.readOnlyText) {
            await this.save(false);
        }
        await this.s_proofreadings.onNextExternState(nextState);
    }

    nextCondition(next: string): boolean | undefined {
        if (!this.externState) return false;
        return this.s_proofreadings.nextCondition(this.externState, next, this.computeUserAttributes);
    }

    // click SAVE PRODUCTION TEXTS
    async save(alert = true) {
        if (this.s_productionTexts.current) {
            await this.s_productionTexts.patchCurrent();
            if (alert) this.s_alert.success("SUCCESS.SAVE");
            await UtilsService.sleep(250);
        }
    }

    // Clic sur APPROVE
    // Dialog APPROVE
    onApprove() {
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: "DIALOG.ASSESSMENT.APPROBATION",
                    text: "DIALOG.ASSESSMENT.APPROBATION_TXT",
                    valid: "YES",
                    noConfirm: false
                }
            })
            .afterClosed()
            .subscribe(async (res) => {
                console.log('ON APPROVE', res);
                if (res) {
                    await this.approve();
                }
            });
    }

    // Lance le Flux APPROVAL mode "accept"
    async approve() {
        if (!this.readOnlyText) {
            await this.save(false);
        }
        if (this.isCPP()) {
            await this.s_proofreadings.approval(ApprovalAssessments.ACCEPT);
        } else if (this.isApproverDocument) {
            await this.s_proofreadings.approvalClient(ApprovalAssessments.ACCEPT);
        } else if (this.isApproverInfo) {
            await this.s_proofreadings.approvalInfo(ApprovalAssessments.ACCEPT);
        }
    }

    // Clic sur REJECT
    // Dialog REJECT
    onReject() {
        this.dialog
            .open(ConfirmDialogComponent, {
                data: {
                    title: "DIALOG.ASSESSMENT.REJECT",
                    text: "DIALOG.ASSESSMENT.REJECT_TXT",
                    valid: "YES",
                    noConfirm: false
                }
            })
            .afterClosed()
            .subscribe(async (res) => {
                console.log('ON APPROVE', res);
                if (res) {
                    await this.reject();
                }
            });
    }

    // Lance le Flux APPROVAL mode "reject"
    async reject() {
        if (!this.readOnlyText) {
            await this.save(false);
        }
        if (this.isCPP()) {
            await this.s_proofreadings.approval(ApprovalAssessments.REJECT);
        } else if (this.isApproverDocument) {
            await this.s_proofreadings.approvalClient(ApprovalAssessments.REJECT);
        } else if (this.isApproverInfo) {
            await this.s_proofreadings.approvalInfo(ApprovalAssessments.REJECT);
        }
    }

    async resetProdAdmin() {
        const res = await firstValueFrom(this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: `RESET_PROD.TITLE`,
                text: `RESET_PROD.TEXT`,
                valid: 'YES'
            }
        }).afterClosed());
        if (res) {
            const reply = await this.s_liveco.create('/admin/reset-intern', { proofreadingID: this.proofreading?._id });
            if (reply) {
                this.s_proofreadings.emitCurrent(new Proofreading(reply));
            }
        }
    }

    getTooltip(): string {
        if (this.project_archived) return 'NOT_ALLOWED_ON_ARCHIVED';
        return '';
    }


    getSlugText(next: string): string {
        const next_state = this.s_state_machine.entities.find(e => e.slug === next && e.type === 'extern' && e.flow.includes(this.proofreading!.flow));
        return next_state?.payload?.text_slug || next;
    }
}
